import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CommentForm from './CommentForm.jsx';

/**
 * Component for displaying a comment and its replies
 */
const CommentThread = ({
  comment,
  onReply,
  onEdit,
  onDelete,
  currentUserId,
  className = '',
  level = 0, // Track nesting level: 0 = top-level, 1+ = replies
  isHighlighted = false, // Whether this comment should be highlighted
  isDarkMode = false
}) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showReplies, setShowReplies] = useState(true);

  const isOwner = currentUserId && comment.user.id === currentUserId;
  const hasReplies = comment.replies && comment.replies.length > 0;
  const isTopLevel = level === 0; // Only top-level comments can receive replies

  /**
   * Handle reply submission
   */
  const handleReplySubmit = async (content) => {
    try {
      await onReply(comment.id, content);
      setShowReplyForm(false);
    } catch (error) {
      console.error('Error submitting reply:', error);
    }
  };

  /**
   * Handle edit submission
   */
  const handleEditSubmit = async (content) => {
    try {
      await onEdit(comment.id, content);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        await onDelete(comment.id);
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    }
  };

  /**
   * Format timestamp
   */
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    
    return date.toLocaleDateString();
  };

  /**
   * Get user initials for avatar
   */
  const getUserInitials = (user) => {
    if (user.name) {
      return user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user.email ? user.email[0].toUpperCase() : 'U';
  };

  return (
    <div className={`comment-thread ${className}`}>
      {/* Main comment */}
      <div
        className="comment-item"
        style={{
          backgroundColor: isHighlighted 
            ? 'rgba(59, 130, 246, 0.1)' 
            : (isDarkMode ? '#374151' : '#ffffff'),
          border: isHighlighted 
            ? '2px solid rgba(59, 130, 246, 0.4)' 
            : `1px solid ${isDarkMode ? '#4a5568' : '#e5e7eb'}`,
          borderRadius: '8px',
          padding: '12px',
          marginBottom: hasReplies ? '8px' : '0',
          transition: 'all 0.2s ease'
        }}
      >
        {/* Comment header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}
        >
          {/* User avatar */}
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'var(--comment-accent, #3b82f6)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '600',
              flexShrink: 0,
              backgroundImage: comment.user.profile_image ? `url(${comment.user.profile_image})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {!comment.user.profile_image && getUserInitials(comment.user)}
          </div>

          {/* User name and timestamp */}
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: '600',
                fontSize: '14px',
                color: isDarkMode ? '#f9fafb' : '#374151'
              }}
            >
              {comment.user.name || comment.user.email}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: isDarkMode ? '#9ca3af' : '#6b7280'
              }}
            >
              {formatTimestamp(comment.createdAt)}
            </div>
          </div>

          {/* Comment actions */}
          {isOwner && (
            <div
              style={{
                display: 'flex',
                gap: '4px',
                opacity: 0.7
              }}
            >
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  padding: '4px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  transition: 'all 0.2s ease'
                }}
                title="Edit comment"
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                  e.target.style.color = '#3b82f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'var(--comment-meta, #6b7280)';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: '4px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  transition: 'all 0.2s ease'
                }}
                title="Delete comment"
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                  e.target.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'var(--comment-meta, #6b7280)';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3,6 5,6 21,6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Comment content */}
        {isEditing ? (
          <CommentForm
            onSubmit={handleEditSubmit}
            onCancel={() => setIsEditing(false)}
            initialContent={comment.content}
            submitLabel="Update"
            cancelLabel="Cancel"
            className="edit-comment-form"
          />
        ) : (
          <div
            style={{
              fontSize: '14px',
              lineHeight: '1.5',
              color: isDarkMode ? '#f9fafb' : '#374151',
              marginBottom: '8px'
            }}
          >
            {comment.content}
          </div>
        )}

        {/* Comment actions */}
        {!isEditing && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '12px',
              color: 'var(--comment-meta, #6b7280)'
            }}
          >
            {/* Only show Reply button for top-level comments */}
            {isTopLevel && (
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                style={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: 'var(--comment-accent, #3b82f6)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                Reply
              </button>
            )}
            
            {hasReplies && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                style={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                {showReplies ? 'Hide' : 'Show'} {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
              </button>
            )}
          </div>
        )}

        {/* Reply form */}
        {showReplyForm && !isEditing && (
          <div style={{ marginTop: '12px' }}>
            <CommentForm
              onSubmit={handleReplySubmit}
              onCancel={() => setShowReplyForm(false)}
              isReply={true}
              placeholder="Write a reply..."
              submitLabel="Reply"
              cancelLabel="Cancel"
              className="reply-comment-form"
            />
          </div>
        )}
      </div>

      {/* Replies */}
      {hasReplies && showReplies && (
        <div
          style={{
            marginLeft: '24px',
            borderLeft: `2px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
            paddingLeft: '16px'
          }}
        >
          {comment.replies.map((reply) => (
            <CommentThread
              key={reply.id}
              comment={reply}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              currentUserId={currentUserId}
              className="reply-thread"
              level={level + 1} // Increment level for nested replies
              isHighlighted={isHighlighted} // Pass highlighting to replies
              isDarkMode={isDarkMode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

CommentThread.propTypes = {
  comment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      email: PropTypes.string.isRequired,
      profile_image: PropTypes.string
    }).isRequired,
    replies: PropTypes.array
  }).isRequired,
  onReply: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  currentUserId: PropTypes.string,
  className: PropTypes.string,
  level: PropTypes.number,
  isHighlighted: PropTypes.bool,
  isDarkMode: PropTypes.bool
};

export default CommentThread;
